"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { apiFetch } from "@/lib/api";

type SubmitResult = { deal_id: number; status: string; already_exists?: boolean };

export default function PostDealPage() {
  const router = useRouter();
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "polling" | "error">("idle");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentDeal, setCurrentDeal] = useState<number | null>(null);
  const [existingDealId, setExistingDealId] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const check = async () => {
      try {
        await apiFetch("/api/v1/me");
      } catch (e: any) {
        if (e.status === 401) {
          router.push(`/login?next=${encodeURIComponent("/post")}`);
        }
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // limpiar polling al desmontar
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const isDealReady = (res: any) =>
    res &&
    (res.status === "active" ||
      res.status === "active_scraped" ||
      res.scrape_status === "ok" ||
      res.is_active === true ||
      res?.deal?.status === "active");

  const isDealFailed = (res: any) =>
    res &&
    (res.status === "failed" ||
      res.status === "invalid" ||
      res.scrape_status === "failed" ||
      res.scrape_status === "error");

  const startPolling = (dealId: number) => {
    setCurrentDeal(dealId);
    setStatus("polling");
    setMsg("Publicando tu oferta...");
    setError(null);

    const controller = new AbortController();
    abortRef.current = controller;
    const timeoutMs = 35000;
    const intervalMs = 1000;
    const start = Date.now();

    const tick = async () => {
      if (controller.signal.aborted) return;
      try {
        const res = await apiFetch(`/api/v1/deals/${dealId}/status`, { signal: controller.signal });
        if (isDealFailed(res)) {
          setStatus("error");
          setMsg(null);
          setError("No pudimos scrapear este link. Intentá nuevamente.");
          return;
        }
        if (isDealReady(res)) {
          router.push(`/deals/${dealId}`);
          return;
        }
      } catch (e: any) {
        if (e?.status === 401) {
          router.push(`/login?next=${encodeURIComponent("/post")}`);
          return;
        }
        if (e?.status && e.status !== 404) {
          setStatus("error");
          setMsg(null);
          setError(e?.message || "Error durante el scrape. Probá de nuevo.");
          return;
        }
      }

      if (Date.now() - start >= timeoutMs) {
        // último intento antes de rendirse
        try {
          const res = await apiFetch(`/api/v1/deals/${dealId}/status`, { signal: controller.signal });
          if (isDealFailed(res)) {
            setStatus("error");
            setMsg(null);
            setError("No pudimos scrapear este link. Intentá nuevamente.");
            return;
          }
          if (isDealReady(res)) {
            router.push(`/deals/${dealId}`);
            return;
          }
        } catch {
          /* ignore */
        }
        setStatus("error");
        setMsg(null);
        setError("No pudimos scrapear este link. Intentá nuevamente.");
        return;
      }
      setTimeout(tick, intervalMs);
    };

    tick();
  };

  const submit = async () => {
    if (!url.trim()) return;
    setExistingDealId(null);
    setStatus("submitting");
    setMsg(null);
    setError(null);
    try {
      const res = (await apiFetch("/api/v1/deals", {
        method: "POST",
        body: { url, description_user: description }
      })) as SubmitResult;
      if (!res || typeof res.deal_id !== "number") {
        throw new Error("Respuesta inválida del servidor");
      }
      if (res.already_exists) {
        setExistingDealId(res.deal_id);
        setStatus("idle");
        setMsg(null);
        setError(null);
        return;
      }
      startPolling(res.deal_id);
    } catch (e: any) {
      if (e?.status === 401) {
        router.push(`/login?next=${encodeURIComponent("/post")}`);
        return;
      }
      const msg =
        e?.status === 429
          ? "Estás yendo muy rápido. Probá de nuevo en un minuto."
          : (typeof e?.message === "string" && e.message) ||
            (typeof e?.detail === "string" && e.detail) ||
            "Error al publicar";
      setStatus("error");
      setError(msg);
      setCurrentDeal(null);
    }
  };

  const cancelPolling = () => {
    abortRef.current?.abort();
    setStatus("idle");
    setMsg(null);
    setError(null);
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="text-2xl font-bold text-ink">Publicar oferta</h1>
      <p className="mt-1 text-sm text-slate-600">
        Pegá la URL de Mercado Libre. Validamos duplicados y scrapeamos la info automáticamente.
      </p>
      {existingDealId && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="flex items-center justify-between gap-3">
            <span>Este deal ya existe.</span>
            <Link
              href={`/deals/${existingDealId}`}
              className="rounded-full border border-amber-300 px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
            >
              Ver deal existente
            </Link>
          </div>
        </div>
      )}
      {msg && <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{msg}</div>}
      {error && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      <div className="mt-4 space-y-3">
        <div>
          <label className="text-sm font-semibold text-slate-700">URL de la oferta</label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 shadow-sm"
            placeholder="https://articulo.mercadolibre.com.ar/..."
          />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700">Descripción breve (opcional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 shadow-sm"
            rows={3}
            maxLength={500}
            placeholder="Qué tiene de bueno, precio final, etc."
          />
          <div className="text-xs text-slate-500">{description.length}/500</div>
        </div>
        {status === "polling" ? (
          <div className="rounded-xl border border-slate-200 bg-mist px-4 py-3 text-sm text-ink">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Publicando tu oferta...</span>
              <span className="text-xs text-slate-600">Esperando confirmación</span>
            </div>
            <p className="mt-1 text-xs text-slate-600">Esto puede tardar unos segundos.</p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={cancelPolling}
                className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-accent hover:text-accent"
              >
                Cancelar
              </button>
              <button
                onClick={() => router.push("/")}
                className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-accent hover:text-accent"
              >
                Volver al inicio
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={submit}
              disabled={status === "submitting" || !url.trim()}
              className="rounded-full bg-ink px-5 py-2 text-sm font-semibold text-white shadow hover:bg-charcoal disabled:opacity-60"
            >
              {status === "submitting" ? "Enviando..." : "Publicar"}
            </button>
            {status === "error" && (
              <button
                onClick={() => {
                  setStatus("idle");
                  setCurrentDeal(null);
                  setError(null);
                  setMsg(null);
                }}
                className="rounded-full border border-accent px-4 py-2 text-sm font-semibold text-accent hover:bg-accent hover:text-midnight"
              >
                Intentar nuevamente
              </button>
            )}
            <button
              onClick={() => router.push("/")}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-accent hover:text-accent"
            >
              Volver al inicio
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
